import { useEffect, useState } from "react";
import axios from "axios";
import {
  Smartphone, RefreshCw, AlertTriangle, CheckCircle2,
  AlertCircle, ExternalLink,
} from "lucide-react";
import { API } from "../../config";

// Colour scale for the success-rate KPI — the only number the ops team
// glances at first thing in the morning. Anything <70% is red, 70-95%
// amber, ≥95% green.
const rateColor = (pct, hasAttempts) => {
  if (!hasAttempts) return "text-gray-500";
  if (pct < 70) return "text-red-400";
  if (pct < 95) return "text-lojack-clause-400";
  return "text-emerald-400";
};

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const truncate = (s, n = 48) => {
  if (!s) return "—";
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

export const WalletRefreshHealthWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = async (windowDays = days) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/analytics/wallet-refresh-health`, {
        params: { days: windowDays },
      });
      setData(res.data);
    } catch (err) {
      console.error("Wallet refresh health fetch failed:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(days);
  }, [days]);

  const hasAttempts = !!data && (
    data.devices_pushed_ok_total + data.devices_pushed_failed_total
  ) > 0;

  return (
    <div
      className="bg-[#0F0F0F] border border-gray-800 rounded-2xl p-6"
      data-testid="wallet-refresh-health-widget"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-lojack-blue-500/15 border border-lojack-blue-500/30 flex items-center justify-center">
            <Smartphone className="w-5 h-5 text-lojack-blue-300" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">
              Cambi paese & Refresh Card (APN)
            </h3>
            <p className="text-gray-500 text-xs">
              Health check delivery push Apple Wallet — alert proattivi su cert scaduti, BadDeviceToken, errori APN
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-[#1A1A1A] text-gray-300 text-xs border border-gray-700 rounded-lg px-2 py-1 cursor-pointer focus:border-lojack-red outline-none"
            data-testid="wallet-refresh-window"
          >
            <option value={7}>Ultimi 7 g</option>
            <option value={30}>Ultimi 30 g</option>
            <option value={90}>Ultimi 90 g</option>
            <option value={365}>Ultimo anno</option>
          </select>
          <button
            type="button"
            onClick={() => fetchData(days)}
            className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
            data-testid="wallet-refresh-refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="py-10 text-center text-gray-500 text-sm">Caricamento health check…</div>
      )}

      {data && (
        <>
          {/* Alert banner — only when something needs attention */}
          {data.alert && (
            <div
              className="mb-5 flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
              data-testid="wallet-refresh-alert"
            >
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm leading-relaxed">{data.alert}</p>
            </div>
          )}

          {/* KPI tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                Broadcast totali
              </p>
              <p className="text-2xl font-bold text-white mt-1" data-testid="wrh-kpi-broadcasts">
                {data.broadcasts_total}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">
                <span className="text-lojack-blue-300 font-mono">{data.broadcasts_country_change}</span> self-service ·{" "}
                <span className="text-gray-400 font-mono">{data.broadcasts_admin}</span> admin
              </p>
            </div>
            <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                Push consegnati
              </p>
              <p className="text-2xl font-bold text-emerald-400 mt-1" data-testid="wrh-kpi-ok">
                {data.devices_pushed_ok_total}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">
                su {data.devices_pushed_ok_total + data.devices_pushed_failed_total} tentativi
              </p>
            </div>
            <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                Push falliti
              </p>
              <p className="text-2xl font-bold text-red-400 mt-1" data-testid="wrh-kpi-failed">
                {data.devices_pushed_failed_total}
              </p>
              <p className="text-[11px] text-gray-500 mt-1">
                {data.broadcasts_with_failures} broadcast con errori
              </p>
            </div>
            <div className="bg-[#141414] border border-gray-800 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-gray-500">
                Tasso successo
              </p>
              <p
                className={`text-2xl font-bold mt-1 ${rateColor(data.success_rate_pct, hasAttempts)}`}
                data-testid="wrh-kpi-success-rate"
              >
                {hasAttempts ? `${data.success_rate_pct}%` : "—"}
              </p>
              <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                {hasAttempts && data.success_rate_pct >= 95 && (
                  <><CheckCircle2 className="w-3 h-3 text-emerald-400" />ottimo</>
                )}
                {hasAttempts && data.success_rate_pct < 95 && data.success_rate_pct >= 70 && (
                  <><AlertCircle className="w-3 h-3 text-lojack-clause-400" />sotto target</>
                )}
                {hasAttempts && data.success_rate_pct < 70 && (
                  <><AlertTriangle className="w-3 h-3 text-red-400" />degrado severo</>
                )}
                {!hasAttempts && "—"}
              </p>
            </div>
          </div>

          {/* Top failure reasons */}
          {data.top_failure_reasons?.length > 0 ? (
            <div className="mb-5">
              <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-2">
                Top motivi di fallimento
              </p>
              <div className="space-y-1.5" data-testid="wrh-top-failure-reasons">
                {data.top_failure_reasons.map((r, i) => (
                  <div
                    key={`${r.reason}-${i}`}
                    className="flex items-center justify-between bg-[#141414] border border-gray-800 rounded-lg px-3 py-2"
                  >
                    <span className="text-gray-300 text-xs font-mono truncate flex-1 mr-3">
                      {truncate(r.reason, 80)}
                    </span>
                    <span className="text-xs bg-red-500/15 text-red-300 border border-red-500/30 rounded-full px-2 py-0.5 font-mono flex-shrink-0">
                      {r.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : data.broadcasts_total > 0 ? (
            <p className="text-emerald-400/80 text-sm italic mb-5 flex items-center gap-2" data-testid="wrh-no-failures">
              <CheckCircle2 className="w-4 h-4" />
              Nessun fallimento nel periodo selezionato.
            </p>
          ) : null}

          {/* Recent failures table */}
          {data.recent_failures?.length > 0 && (
            <div className="mb-5">
              <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-2">
                Ultimi broadcast con errori
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="wrh-recent-failures">
                  <thead>
                    <tr className="text-gray-500 text-xs">
                      <th className="text-left py-2 px-2 font-medium">Quando</th>
                      <th className="text-left py-2 px-2 font-medium">Motivo</th>
                      <th className="text-left py-2 px-2 font-medium">Trigger</th>
                      <th className="text-right py-2 px-2 font-medium">OK / Tot</th>
                      <th className="text-left py-2 px-2 font-medium">Primo errore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_failures.map((f, i) => (
                      <tr key={`${f.started_at}-${i}`} className="border-t border-gray-800/60">
                        <td className="py-2 px-2 text-gray-300 text-xs whitespace-nowrap">
                          {formatDate(f.started_at)}
                        </td>
                        <td className="py-2 px-2 text-gray-200 font-mono text-xs">
                          {truncate(f.reason, 40)}
                        </td>
                        <td className="py-2 px-2 text-gray-400 text-xs">
                          {f.triggered_by === "customer_self_service" ? "🧑 cliente" : "👤 admin"}
                          {f.is_smoke_test && (
                            <span className="ml-1 text-[10px] bg-gray-700 text-gray-300 rounded px-1.5 py-0.5">
                              smoke
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-xs">
                          <span className="text-emerald-400">{f.devices_pushed_ok}</span>
                          <span className="text-gray-600 mx-1">/</span>
                          <span className="text-gray-300">{f.devices_total}</span>
                        </td>
                        <td className="py-2 px-2 text-red-300 text-xs font-mono" title={f.first_fail_reason || ""}>
                          {truncate(f.first_fail_reason, 36)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily volume sparkline */}
          {data.daily_volume?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-2">
                Volume giornaliero (ultimi {days} g)
              </p>
              <div className="flex items-end gap-1 h-20" data-testid="wrh-daily-sparkline">
                {(() => {
                  const max = Math.max(
                    ...data.daily_volume.map((d) => d.broadcasts), 1,
                  );
                  return data.daily_volume.map((d) => {
                    const okPct = d.broadcasts ? (d.ok / d.broadcasts) * 100 : 0;
                    const failPct = d.broadcasts ? (d.failed / Math.max(d.broadcasts, 1)) * 100 : 0;
                    const heightPct = (d.broadcasts / max) * 100;
                    const isFail = d.failed > 0;
                    return (
                      <div
                        key={d.day}
                        className="flex-1 flex flex-col justify-end items-center group relative"
                        title={`${d.day}: ${d.broadcasts} broadcast (${d.ok} ok · ${d.failed} fail)`}
                      >
                        <div
                          className={`w-full rounded-t transition-all ${
                            isFail ? "bg-red-500/60 hover:bg-red-500/80" : "bg-lojack-blue-400/60 hover:bg-lojack-blue-400/90"
                          }`}
                          style={{ height: `${Math.max(heightPct, 4)}%` }}
                        />
                        <div className="absolute -bottom-5 text-[9px] text-gray-600 font-mono">
                          {d.day.slice(5)}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {data.broadcasts_total === 0 && (
            <p className="text-gray-500 text-sm italic mt-3" data-testid="wrh-no-broadcasts">
              Nessun broadcast wallet refresh registrato nel periodo selezionato.
            </p>
          )}

          {/* Cross-link to the existing manual broadcast UI */}
          <div className="mt-4 pt-4 border-t border-gray-800/60 flex items-center justify-between text-xs">
            <p className="text-gray-500">
              I broadcast self-service partono in automatico al cambio paese cliente.
              Per inviare un push manuale (es. dopo aggiornamento template),
              usa la pagina dedicata Apple PassKit.
            </p>
            <a
              href="/admin/apple-passkit"
              className="text-lojack-blue-300 hover:text-lojack-blue-200 font-medium flex items-center gap-1 flex-shrink-0 ml-3"
              data-testid="wrh-link-apple-passkit"
            >
              Apple PassKit <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </>
      )}
    </div>
  );
};
